/**
 * Phase 15.6P — useCurrentUser hook.
 * Phase 21.3a5 — Now listens for the global 'rgm:user-updated' event so
 * every page instance re-fetches /auth/me the moment a profile photo /
 * banner / bio is saved. Fixes the "avatar still shows the old photo
 * everywhere except the edit page" bug without a shared React context.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Fire this whenever the current user's profile is mutated (photo, banner,
 * name, presence). Every mounted useCurrentUser will re-fetch /auth/me.
 */
export const notifyUserUpdated = () => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("rgm:user-updated"));
    }
  } catch (_e) { /* SSR / restricted env */ }
};

export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/me");
      // Backend variants — some endpoints return user inline, others nest it.
      setCurrentUser(r.data?.user || r.data || null);
    } catch (_e) {
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!alive) return;
        setCurrentUser(r.data?.user || r.data || null);
      } catch (_e) {
        if (!alive) return;
        setCurrentUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    // Phase 21.3a5 — Global refresh trigger. When any component fires
    // notifyUserUpdated() (e.g., after a successful profile photo save),
    // every mounted useCurrentUser instance re-fetches /auth/me so the
    // fresh photo lands on the header, cards, comments, etc. — without
    // a full page reload.
    const onUpdated = () => { if (alive) refresh(); };
    if (typeof window !== "undefined") {
      window.addEventListener("rgm:user-updated", onUpdated);
    }
    return () => {
      alive = false;
      if (typeof window !== "undefined") {
        window.removeEventListener("rgm:user-updated", onUpdated);
      }
    };
  }, [refresh]);

  return { currentUser, loading, refresh };
};

export default useCurrentUser;
